/**
 * PatientRefundPaymentDialog — refund an advance receipt from the Patient
 * Billing → Advance receipts sub-tab.
 *
 * Flutter ref: `refund_payment_popup.dart` (`RefundPaymentPopUp`). This is a
 * DISTINCT widget from the billing-module invoice-payment refund
 * (`billing/components/refund-payment-dialog.tsx`) — Flutter ships a
 * separate popup for the patient advance-receipt flow, so we mirror it
 * rather than overloading the invoice dialog:
 *
 *   - Max refundable = min(receiptAmount, availableBalance)  (Flutter
 *     `refund_payment_popup.dart:171-184` validates against BOTH).
 *   - Refund reason is a MANDATORY dropdown over the REFUND value-set
 *     (`useRefundReasons`), shipped as `refundReason: { id, value }`.
 *   - Methods are Cash / Card / Bank (verbatim `payment_method_*`),
 *     default Cash. (No Cheque here — the invoice dialog has it; this
 *     popup does not.)
 *
 * Wire (verified 2026-06-02 dev curl): reuses `useRefundPayment` —
 *   POST /Billing/Payment/Refund/{receiptId}
 *        body: { refundAmount, refundTo, refundReason:{id,value}, refundedById }
 *        → { refundId, status, errorDetails }
 *
 * `useRefundPayment` invalidates the billing-module keys; this host also
 * invalidates the patient-billing list + balance caches on success.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Modal, Select } from '@scripsteam/scrips-signal-ds';
import { useAuthStore } from '@/auth/auth-store';
import { useRefundPayment } from '@/billing/services/use-payments';
import { useRefundReasons } from '@/billing/services/use-refund-reasons';
import { handlePaymentError } from '@/billing/lib/payment-errors';
import type { AdvancePaymentReceipt } from '@/shared/types/billing';

import { formatMoney } from '@/shared/lib/money';
// Verbatim Flutter `payment_method_*` (`app_constants.dart:744-746`).
const REFUND_TO_METHODS: { value: string; label: string }[] = [
  { value: 'Cash', label: 'Cash' },
  { value: 'Card', label: 'Card' },
  { value: 'Bank', label: 'Bank' },
];

export interface PatientRefundPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  receipt: AdvancePaymentReceipt;
  patientId: string;
  /** Optional cashier-shift escalation (PROD-798) — see ReceivePaymentDialog. */
  onCashierShiftRequired?: () => void;
}

export function PatientRefundPaymentDialog({
  open,
  onOpenChange,
  receipt,
  patientId,
  onCashierShiftRequired,
}: PatientRefundPaymentDialogProps) {
  const userId = useAuthStore((s) => s.user?.userId);
  const qc = useQueryClient();
  const refundPayment = useRefundPayment();
  // Destructure the stable `reset` callback. Depending on the whole
  // mutation object in the reset effect below would loop: the object gets a
  // fresh identity every render, so the effect would re-run → reset() →
  // re-render → infinite "Maximum update depth". `reset` is referentially
  // stable across renders, so the effect settles after the first run.
  const { reset: resetRefund } = refundPayment;
  const reasonsQuery = useRefundReasons(open);
  const reasons = reasonsQuery.data ?? [];

  // Flutter clamps the refund to BOTH the receipt amount and the still-
  // available balance — you can only refund what hasn't been allocated.
  const maxRefund = useMemo(() => {
    const amt = receipt.receiptAmount ?? 0;
    const avail = receipt.availableBalance ?? 0;
    return Math.min(amt, avail);
  }, [receipt.receiptAmount, receipt.availableBalance]);

  const [refundTo, setRefundTo] = useState('Cash');
  const [amount, setAmount] = useState('');
  const [reasonId, setReasonId] = useState('');

  useEffect(() => {
    if (open) {
      setRefundTo('Cash');
      setAmount(maxRefund > 0 ? String(maxRefund) : '');
      setReasonId('');
      resetRefund();
    }
  }, [open, maxRefund, resetRefund]);

  const parsed = Number.parseFloat(amount);
  const validAmount =
    !Number.isNaN(parsed) && parsed > 0 && parsed <= maxRefund + 0.01;
  const selectedReason = reasons.find((r) => r.id === reasonId);
  const canSubmit =
    validAmount && !!selectedReason?.id && !!receipt.id && !refundPayment.isPending;

  const submit = async () => {
    if (!canSubmit || !receipt.id || !selectedReason) return;
    try {
      await refundPayment.mutateAsync({
        receiptId: receipt.id,
        invoiceId: receipt.invoiceId ?? undefined,
        maxAmount: maxRefund,
        request: {
          refundAmount: parsed,
          refundTo,
          refundReason: {
            id: selectedReason.id ?? undefined,
            value: selectedReason.value ?? undefined,
          },
          refundedById: userId,
        },
      });
      // `useRefundPayment` refreshes the billing-module caches; the patient
      // billing surface owns these three (balance + the two lists).
      void qc.invalidateQueries({
        queryKey: ['patient-advance-receipts', patientId],
      });
      void qc.invalidateQueries({
        queryKey: ['patient-available-balance', patientId],
      });
      void qc.invalidateQueries({
        queryKey: ['patient-refunds-list', patientId],
      });
      onOpenChange(false);
    } catch (err) {
      if (handlePaymentError(err, onCashierShiftRequired) === 'handled') {
        onOpenChange(false);
        return;
      }
      /* Global toast surfaces failure; dialog stays open so the user can retry. */
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title="Refund Receipt"
      description={<>Refund up to{' '}
            <span className="font-medium text-text-primary">
              {formatMoney(maxRefund)}
            </span>{' '}
            from receipt{' '}
            <span className="font-medium text-text-primary">
              {receipt.receiptNumber ?? receipt.id}
            </span>
            .</>}
      size="md"
      footer={<><Button
            variant="tertiary"
            onClick={() => onOpenChange(false)}
            disabled={refundPayment.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="bg-status-error hover:bg-status-error/90 text-white"
          >
            {refundPayment.isPending ? 'Refunding…' : 'Refund'}
          </Button></>}
    >
        

        <div data-slot="payment-form">
          <div data-slot="payment-field">
            <label data-slot="payment-label">
              Refund amount
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              placeholder="Enter amount"
              onChange={(e) => setAmount(e.target.value)}
              data-slot="payment-input"
              className="number-spinner-none"
            />
            {amount && !validAmount && (
              <p data-slot="payment-error">
                Amount must be greater than 0 and cannot exceed receipt and
                available amount ({formatMoney(maxRefund)}).
              </p>
            )}
          </div>

          <div data-slot="payment-field">
            <label data-slot="payment-label">
              Refund to
            </label>
            <div data-slot="field-pills">
              {REFUND_TO_METHODS.map((m) => {
                const active = refundTo === m.value;
                return (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setRefundTo(m.value)}
                    data-slot="field-pill"
                    data-active={active ? 'true' : 'false'}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div data-slot="payment-field">
            <label data-slot="payment-label">
              Refund reason
            </label>
            <Select
              value={reasonId}
              onChange={(v) => setReasonId(v ?? '')}
              size="compact"
              placeholder={reasonsQuery.isLoading ? 'Loading…' : 'Select reason'}
              options={reasons.map((r) => ({
                label: r.value ?? '',
                value: r.id ?? '',
              }))}
            />
          </div>
        </div>

        
      </Modal>
  );
}
